import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { trackEvent } from '@/components/Analytics';
import { getMe } from '@/lib/bootRequests';
import { hydrate } from '@/lib/portfolioStore';
import { prefetchGet } from '@/lib/dedupedGet';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth'd
  const [loading, setLoading] = useState(true);
  // Feb 2026 (Round P111t) — "View as Free User" preview toggle.
  //
  // The site owner's email lives on `_BASELINE_ADMIN_EMAILS` in
  // `backend/server.py`, so every login (basic or Google OAuth)
  // auto-promotes their account to role='admin'. Great for shipping,
  // terrible for iterating on the paywall UX because the site owner
  // can never see what a real Free user sees without logging out.
  //
  // When `viewAsFree` is on, `user.role` is shadowed to 'user' for
  // every downstream consumer (Dashboard filter locks, screener link
  // gating, watchlist custom-list rendering, StockModal gated tabs).
  // Backend data payloads still reflect the true admin role (so
  // owner tools like AdminReconcileTierCutsButton keep working) —
  // this preview is a UI-only overlay meant to answer "what does a
  // logged-in Free user see right now?". Persists in sessionStorage
  // so a refresh doesn't rug-pull the preview mid-flow.
  const [viewAsFree, _setViewAsFree] = useState(() => {
    try { return window.sessionStorage.getItem('divi:viewAsFree') === '1'; }
    catch (_e) { return false; }
  });
  const setViewAsFree = useCallback((next) => {
    _setViewAsFree(!!next);
    try { window.sessionStorage.setItem('divi:viewAsFree', next ? '1' : '0'); }
    catch (_e) { /* sessionStorage unavailable — silent */ }
  }, []);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const data = await getMe();
      setUser(data);
      // Sep 2026 — warm the always-live portfolio headline the moment we know
      // who the user is, so the My Portfolio card paints from a request that
      // left before the page's other ~100 fetches.
      hydrate().then((s) => {
        const params = s?.activeId ? { portfolio_id: s.activeId } : {};
        prefetchGet(`${API}/portfolio/headline`, { params, withCredentials: true });
      }).catch(() => {});
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const register = async (email, password, name, ref = null) => {
    const payload = { email, password, name };
    if (ref) payload.ref = ref;
    const { data } = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
    setUser(data);
    trackEvent('sign_up', { method: 'email' });
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(false);
  };

  const setAuthUser = (userData) => setUser(userData);

  // Effective user for UI gating. When viewAsFree is ON and the real
  // account is admin, shadow role → 'user' so every downstream
  // `user?.role === 'admin'` check treats the session as Free-tier.
  // Real admin object stays in `realUser` for the header banner.
  const effectiveUser = React.useMemo(() => {
    if (!user || typeof user !== 'object') return user;
    if (viewAsFree && user.role === 'admin') {
      return { ...user, role: 'user', _viewAsFree: true };
    }
    return user;
  }, [user, viewAsFree]);
  const isRealAdmin = !!(user && typeof user === 'object' && user.role === 'admin');

  return (
    <AuthContext.Provider value={{
      user: effectiveUser,
      realUser: user,
      loading,
      login, register, logout, setAuthUser, checkAuth,
      viewAsFree, setViewAsFree, isRealAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
